import {
    HTTP_INTERCEPTORS,
    HttpClient,
    provideHttpClient,
} from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { DatePipe } from '@angular/common';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAnalyticsModule } from '@angular/fire/compat/analytics';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { JWT_OPTIONS, JwtHelperService } from '@auth0/angular-jwt';
import {
    KeycloakAngularModule,
    KeycloakBearerInterceptor,
    KeycloakService,
} from '@inoventoryapps/keycloak-angular';
import { provideHighcharts } from 'highcharts-angular';
import {
    NgxUiLoaderConfig,
    NgxUiLoaderHttpModule,
    NgxUiLoaderModule,
} from 'ngx-ui-loader';
import { environment } from '../environments/environment';
import { AuthService } from './_auth/auth.service';
import { defaultLang } from './_constants/appSetting.constant';
import { initializeKeycloak } from './_init/keycloak-init.factory';
import { CacheInterceptor } from './_interceptor/cache-interceptor';
import { HttpErrorHandlerInterceptor } from './_interceptor/http-error-handler.interceptor';
import { HttpHeaderConfigInterceptor } from './_interceptor/http-header-config.interceptor';
import { SharedModule } from './_modules/shared/shared.module';
import { KeyService } from './_services/_utils/key.service';
import { CacheResolverService } from './_services/cache-resolver.service';
import { EncryptionInterceptor } from './_services/encryption.service';
import { CustomRouteReuseStrategy } from './_services/route-reuse.strategy';
import { AppRoutingModule, routes } from './app-routing.module';
import { AppComponent } from './app.component';

export const HttpLoaderFactory = (http: HttpClient) => {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
};

export function InitIndexDB(keyService: KeyService) {
    return (): Promise<any> => {
        return keyService.initDB();
    };
}

const I18N_CONFIG = {
    defaultLanguage: defaultLang.langCode,
    loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
    },
};

const ngxUiLoaderConfig: NgxUiLoaderConfig = {
    bgsSize: 0,
    fgsSize: 0,
    logoSize: 120,
    hasProgressBar: false,
    logoUrl: 'https://cdn.lokos.in/lokos-in/assets/LokOS-Circle-Animation.gif',
    overlayColor: 'transparent',
    text: '',
    masterLoaderId: 'master',
};

@NgModule({
    declarations: [AppComponent],

    imports: [
        BrowserModule,
        AppRoutingModule,
        TranslateModule.forRoot(I18N_CONFIG),
        NgxUiLoaderHttpModule.forRoot({ showForeground: true }),
        NgxUiLoaderModule.forRoot(ngxUiLoaderConfig),
        KeycloakAngularModule,
        FlexLayoutModule,
        AngularFireModule.initializeApp(environment.firebase),
        AngularFireAnalyticsModule,
        SharedModule,
    ],

    providers: [
        {
            provide: APP_INITIALIZER,
            useFactory: initializeKeycloak,
            multi: true,
            deps: [KeycloakService],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: InitIndexDB,
            deps: [KeyService],
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: KeycloakBearerInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpHeaderConfigInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorHandlerInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CacheInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: EncryptionInterceptor,
            multi: true,
        },
        {
            provide: MAT_DATE_LOCALE,
            useValue: 'en-GB',
        },
        DatePipe,
        AuthService,
        { provide: JWT_OPTIONS, useValue: JWT_OPTIONS },
        JwtHelperService,
        provideAnimationsAsync(),
        provideRouter(routes),
        provideHttpClient(),
        provideHighcharts(),
        CacheResolverService,
        {
            provide: RouteReuseStrategy,
            useClass: CustomRouteReuseStrategy,
        },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
